/*!
 * textSplit.js — a text's content cut into its items: lines, stanzas, words, characters
 * © 2026 Rob Canning — GPLv3
 *
 * text() (text.js) shows them one at a time. Pure, so node tests it.
 *
 *     "one; two"              lines (a ; separates them inline too)
 *     "3:hello 2:world"       a leading number is the item's beats (word, line, stanza)
 *     wrap:0                  lines kept as written: indentation, blank lines in a stanza
 */

import { secPerBeat } from "./beats.js";

/**
 * The items of `str` by `unit` (line, stanza | group, word, char):
 * [{ text, dur }], dur in seconds or null. opts.wrap === false keeps the
 * lines as written; opts.k is the seconds of a beat (the tempo's, by default).
 */
export function splitContent(str, unit, opts = {}) {
  const keepLines = opts.wrap === false;
  if (!str) return [];
  // "3:hello" -- the 3 is beats
  const k = opts.k ?? secPerBeat();

  if (unit === "word") {
    // Support duration:word pairs like "3:hello 2:world"
    return str.split(/\s+/).filter(Boolean).map(tok => {
      const match = tok.match(/^(\d+(?:\.\d+)?):(.+)$/);
      if (match) {
        return { text: match[2], dur: Number(match[1]) * k };
      }
      return { text: tok, dur: null };
    });
  }

  if (unit === "char") {
    return str.split("").filter(ch => ch.trim()).map(ch => ({ text: ch, dur: null }));
  }

  if (unit === "stanza" || unit === "group") {
    // Split by blank lines (double newlines) to get groups/stanzas
    const groups = str.split(/\n\s*\n/).filter(s => s.trim());

    return groups.map(group => {
      // With wrapping off the lines are taken as written — indentation and
      // blank lines inside a stanza are part of the layout, not noise.
      let lines;
      if (keepLines) {
        lines = group.split(/\n/).map(l => l.replace(/\r$/, ""));
        // a file's final newline terminates the last line, it does not add a
        // blank one after it
        while (lines.length > 1 && lines[lines.length - 1].trim() === "") lines.pop();
      } else {
        lines = group.split(/\n/).map(l => l.trim()).filter(Boolean);
      }
      if (!lines.length) return null;

      // Check first line for duration prefix
      const firstLine = lines[0];
      const match = firstLine.match(/^(\d+(?:\.\d+)?):(.+)$/);

      if (match) {
        // Duration on first line - apply to whole group
        lines[0] = match[2];
        return { text: lines.join('\n'), dur: Number(match[1]) * k };
      }

      return { text: lines.join('\n'), dur: null };
    }).filter(Boolean);
  }

  // Default: line mode - each line is a separate item
  // Semicolon also works as separator for inline content
  return str.split(/[\r\n;]+/).filter(l => keepLines ? l.trim() : l).map(line => {
    // A duration prefix is read off the trimmed line either way; what the
    // line KEEPS is what differs, so wrap:0 holds on to its indentation.
    const trimmed = line.trim();
    const match = trimmed.match(/^(\d+(?:\.\d+)?):(.+)$/);
    if (match) {
      return { text: match[2], dur: Number(match[1]) * k };
    }
    return { text: keepLines ? line.replace(/\r$/, "") : trimmed, dur: null };
  });
}
